<template>
  <div class="chat-input-content">
    <VaButton icon="mso-smart_toy" icon-color="#54656f" color="transparent" @click="openChat" />
    <input
      ref="launcherRef"
      v-model="launcherText"
      type="text"
      placeholder="Escribe un mensaje..."
      class="chat-open-input"
      @click="openChat"
      @keyup.enter="submitFromLauncher"
    />
    <VaButton icon-color="#54656f" icon="mso-help" color="transparent" @click="showHelpModal = true" />
  </div>

  <div v-if="isVisible" class="wa-overlay" @click.self="isVisible = false">
    <section class="wa-window" role="dialog" aria-label="Asistente SpendFit" @keydown.esc="onEscape">
      <header class="wa-header">
        <div class="wa-avatar" aria-hidden="true">
          <VaIcon name="smart_toy" size="22px" />
        </div>
        <div class="wa-header-text">
          <strong>Asistente SpendFit</strong>
          <span>{{ sending ? 'escribiendo...' : 'en línea' }}</span>
        </div>
        <button type="button" class="wa-header-close" aria-label="Cerrar chat" @click="isVisible = false">
          <VaIcon name="close" size="22px" />
        </button>
      </header>

      <div ref="chatMessagesRef" class="wa-thread" role="log" aria-live="polite">
        <div class="wa-day">Hoy</div>
        <div
          v-for="(msg, index) in messages"
          :key="index"
          class="wa-row"
          :class="msg.fromUser ? 'wa-row--out' : 'wa-row--in'"
        >
          <div class="wa-card" :class="msg.fromUser ? 'wa-card--out' : 'wa-card--in'">
            <div v-if="msg.urlAudio" class="wa-media">
              <AudioPlayer :audio-u-r-l="msg.urlAudio" />
            </div>
            <img v-if="msg.image" :src="msg.image" alt="Imagen enviada" />
            <p v-if="msg.text" class="wa-text">
              <template v-for="(part, partIndex) in textParts(msg.text)" :key="partIndex">
                <a v-if="part.kind === 'link'" :href="part.value" target="_blank" rel="noopener noreferrer">{{
                  part.value
                }}</a>
                <strong v-else-if="part.kind === 'bold'">{{ part.value }}</strong>
                <em v-else-if="part.kind === 'italic'">{{ part.value }}</em>
                <s v-else-if="part.kind === 'strike'">{{ part.value }}</s>
                <code v-else-if="part.kind === 'mono'">{{ part.value }}</code>
                <template v-else>{{ part.value }}</template>
              </template>
              <span class="wa-meta">
                {{ msg.time }}
                <svg v-if="msg.fromUser" viewBox="0 0 16 11" width="16" height="11" aria-hidden="true">
                  <path fill="currentColor" d="M11.1 1.1 4.6 7.6 2.3 5.3 1 6.6l3.6 3.6L12.4 2.4z" />
                  <path fill="currentColor" d="M15 1.1 8.5 7.6l-.7-.7-1.3 1.3 2 2 7.8-7.8z" />
                </svg>
              </span>
            </p>
            <span v-else class="wa-meta wa-meta--alone">
              {{ msg.time }}
              <svg v-if="msg.fromUser" viewBox="0 0 16 11" width="16" height="11" aria-hidden="true">
                <path fill="currentColor" d="M11.1 1.1 4.6 7.6 2.3 5.3 1 6.6l3.6 3.6L12.4 2.4z" />
                <path fill="currentColor" d="M15 1.1 8.5 7.6l-.7-.7-1.3 1.3 2 2 7.8-7.8z" />
              </svg>
            </span>
            <div v-if="pendingChoices(msg)" class="wa-choices">
              <template v-if="usesButtons(msg.options)">
                <button
                  v-for="opt in msg.options"
                  :key="opt.id || opt.title"
                  type="button"
                  class="wa-choice"
                  :disabled="sending"
                  @click="chooseOption(msg, opt)"
                >
                  {{ opt.title }}
                </button>
              </template>
              <button v-else type="button" class="wa-choice" :disabled="sending" @click="openList(msg)">
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M4 6h2v2H4V6zm4 0h12v2H8V6zM4 11h2v2H4v-2zm4 0h12v2H8v-2zM4 16h2v2H4v-2zm4 0h12v2H8v-2z"
                  />
                </svg>
                Ver opciones
              </button>
            </div>
          </div>
        </div>
        <div v-if="sending" class="wa-row wa-row--in">
          <div class="wa-card wa-card--in wa-typing" aria-label="Escribiendo">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>

      <form class="wa-composer" @submit.prevent="sendMessage">
        <VaFileUpload
          v-model="image"
          type="single"
          file-types="image/*"
          hide-file-list
          class="wa-attach"
          @fileAdded="sendImage"
        >
          <template #default="{ openFileDialog }">
            <button
              type="button"
              class="wa-icon-btn"
              aria-label="Adjuntar imagen"
              :disabled="sending"
              @click="openFileDialog"
            >
              <VaIcon name="image" size="22px" />
            </button>
          </template>
        </VaFileUpload>
        <label class="wa-field">
          <input
            ref="composerRef"
            v-model="newMessage"
            type="text"
            maxlength="255"
            :placeholder="$t('form.labels.sendMessage')"
            :disabled="sending"
            autocomplete="off"
          />
        </label>
        <button type="submit" class="wa-send" aria-label="Enviar" :disabled="sending || !newMessage.trim()">
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path fill="currentColor" d="M3.4 20.4 21 12 3.4 3.6 3.3 10.2 15.2 12 3.3 13.8z" />
          </svg>
        </button>
      </form>

      <div v-if="listMessage" class="wa-sheet-backdrop" @click.self="listMessage = null">
        <div class="wa-sheet" role="dialog" aria-label="Opciones">
          <div class="wa-sheet-head">
            <button type="button" class="wa-icon-btn" aria-label="Cerrar opciones" @click="listMessage = null">
              <VaIcon name="close" size="22px" />
            </button>
            <strong>Ver opciones</strong>
          </div>
          <label class="wa-search">
            <VaIcon name="search" size="18px" />
            <input v-model="listQuery" type="text" placeholder="Buscar" autocomplete="off" />
          </label>
          <p class="wa-section">Opciones</p>
          <div class="wa-sheet-list">
            <p v-if="!filteredOptions.length" class="wa-empty">No hay resultados</p>
            <button
              v-for="opt in filteredOptions"
              :key="opt.id || opt.title"
              type="button"
              class="wa-option"
              @click="chooseOption(listMessage, opt)"
            >
              <span>
                <strong>{{ opt.title }}</strong>
                <small v-if="opt.description && opt.description !== opt.title">{{ opt.description }}</small>
              </span>
              <i class="wa-radio" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>

  <VaModal v-slot="{ hide }" v-model="showHelpModal" fullscreen hide-default-actions>
    <div class="flex flex-col justify-center items-center min-h-[95vh] p-4">
      <div class="w-full md:w-8/12 flex flex-col md:flex-row gap-6 items-center">
        <div class="flex-1 flex flex-col justify-center items-start">
          <VaBadge text="🤖¡Hola!" color="success" class="mb-2" />
          <h1 class="text-3xl font-bold mb-2">Soy tu Asistente Financiero Inteligente</h1>
          <p class="mb-4">
            Estoy aquí para ayudarte, puedes interactuar conmigo como prefieras: enviando imágenes de comprobantes,
            escribiendo un mensaje de texto o audio para:
          </p>
          <ul class="list-none pl-0 mb-4" style="line-height: 1.6">
            <li class="flex items-center gap-2"><VaIcon name="check" /> Registrar comprobantes de ingreso</li>
            <li class="flex items-center gap-2">
              <VaIcon name="check" /> Registrar comprobantes de egreso (incluso desde una foto del documento)
            </li>
            <li class="flex items-center gap-2"><VaIcon name="check" /> Generar facturas electrónicas en segundos</li>
            <li class="flex items-center gap-2">
              <VaIcon name="check" /> Crear resúmenes financieros con tus ingresos y egresos
            </li>
            <li class="flex items-center gap-2">
              <VaIcon name="check" /> Ofrecer sugerencias inteligentes, para mejorar el manejo de tus finanzas
            </li>
          </ul>
          <p class="mb-4">
            📲 También puedes <a href="/preferences" class="text-blue-600 underline">Sincronizar</a> tu
            <strong>WhatsApp</strong> para registrar tus ingresos y egresos de manera sencilla. Estoy disponible 24/7
            para ayudarte.
          </p>
        </div>
        <div class="flex-1 flex justify-center items-center">
          <img src="/ejemplo_ia.png" alt="Asistente Financiero" class="max-w-full h-auto" />
        </div>
      </div>
      <div class="w-full md:w-8/12 flex items-center">
        <div class="flex gap-4 mt-4">
          <VaButton class="rounded-full font-semibold w-fit px-8 py-1" @click="openChat">
            <span class="flex items-center gap-2">Abrir Chat</span>
          </VaButton>
          <VaButton color="transparent" class="btn-gmail rounded-full font-semibold w-fit px-4 py-1" @click="hide">
            <span class="flex items-center gap-2 text-black">Cerrar</span>
          </VaButton>
        </div>
      </div>
    </div>
  </VaModal>
  <ExpenseFormModal v-model="showExpense" :draft="paymentDraft" @saved="onFlowSaved" />
  <IncomeFormModal v-model="showIncome" :draft="paymentDraft" @saved="onFlowSaved" />
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { send, sendImageAPI } from '../../services/chat'
import { useUserStore } from '../../stores/user'
import AudioPlayer from '../../components/AudioPlayer.vue'
import ExpenseFormModal from '../finanzas/components/ExpenseFormModal.vue'
import IncomeFormModal from '../finanzas/components/IncomeFormModal.vue'
import type { ChatFlowDraft } from '../../types/chat'

type ChatOption = {
  id: string
  title: string
  description: string
  flow?: ChatFlowDraft
}

type ChatMessage = {
  text: string
  fromUser: boolean
  time: string
  urlAudio?: string
  image?: string
  options?: ChatOption[]
  optionsUsed?: boolean
}

type TextPart = { kind: 'text' | 'link' | 'bold' | 'italic' | 'strike' | 'mono'; value: string }

const chatMessagesRef = ref<HTMLElement | null>(null)
const composerRef = ref<HTMLInputElement | null>(null)
const launcherRef = ref<HTMLInputElement | null>(null)
const isVisible = ref(false)
const userStore = useUserStore()
const userName = userStore.apiUser?.name?.trim() || 'Usuario'
const newMessage = ref('')
const launcherText = ref('')
const sending = ref(false)
const image = ref<File>()
const showHelpModal = ref(false)
const listMessage = ref<ChatMessage | null>(null)
const listQuery = ref('')
const showExpense = ref(false)
const showIncome = ref(false)
const paymentDraft = ref<ChatFlowDraft | null>(null)
const router = useRouter()

const messages = ref<ChatMessage[]>([
  {
    text: `Hola ${userName} 👋! Soy tu asistente inteligente de gestión financiera. Puedo ayudarte a generar facturas, comprobantes de ingresos y egresos de forma rápida y segura mediante texto, imágenes (solo para egresos) y voz.`,
    fromUser: false,
    time: clock(),
  },
])

const filteredOptions = computed(() => {
  const options = listMessage.value?.options ?? []
  const query = listQuery.value.trim().toLowerCase()
  if (!query) return options
  return options.filter((opt) => `${opt.title} ${opt.description}`.toLowerCase().includes(query))
})

watch(isVisible, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) scrollToBottom()
})

onUnmounted(() => {
  document.body.style.overflow = ''
})

function clock() {
  return new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })
}

const waMarker = { '*': 'bold', _: 'italic', '~': 'strike' } as const

function isFormatEdge(char: string | undefined, closing = false) {
  if (char == null) return true
  return closing ? /[\s.,!?;:)\]}"'»]/.test(char) : /[\s([{"'«]/.test(char)
}

function formatInline(text: string): TextPart[] {
  const parts: TextPart[] = []
  let plainStart = 0
  let i = 0

  const pushPlain = (end: number) => {
    if (end > plainStart) parts.push({ kind: 'text', value: text.slice(plainStart, end) })
  }

  while (i < text.length) {
    if (text.startsWith('```', i)) {
      const close = text.indexOf('```', i + 3)
      const inner = close === -1 ? '' : text.slice(i + 3, close)
      const prev = i === 0 ? undefined : text[i - 1]
      const next = close === -1 ? undefined : text[close + 3]
      if (inner.trim() && inner === inner.trim() && isFormatEdge(prev) && isFormatEdge(next, true)) {
        pushPlain(i)
        parts.push({ kind: 'mono', value: inner })
        i = close + 3
        plainStart = i
        continue
      }
    }

    const marker = text[i] as keyof typeof waMarker
    const kind = waMarker[marker]
    if (kind && isFormatEdge(i === 0 ? undefined : text[i - 1]) && text[i + 1] && !/\s/.test(text[i + 1])) {
      const close = text.indexOf(marker, i + 1)
      let found = -1
      let search = close
      while (search !== -1) {
        const inner = text.slice(i + 1, search)
        const next = text[search + 1]
        if (inner && inner === inner.trim() && isFormatEdge(next, true)) {
          found = search
          break
        }
        search = text.indexOf(marker, search + 1)
      }
      if (found !== -1) {
        pushPlain(i)
        parts.push({ kind, value: text.slice(i + 1, found) })
        i = found + 1
        plainStart = i
        continue
      }
    }
    i += 1
  }

  pushPlain(text.length)
  return parts
}

function textParts(text: string): TextPart[] {
  if (!text) return []
  const parts: TextPart[] = []
  const re = /(https?:\/\/[^\s]+)/g
  let last = 0
  let match: RegExpExecArray | null
  while ((match = re.exec(text))) {
    if (match.index > last) parts.push(...formatInline(text.slice(last, match.index)))
    parts.push({ kind: 'link', value: match[1] })
    last = match.index + match[1].length
  }
  if (last < text.length) parts.push(...formatInline(text.slice(last)))
  return parts
}

function normalizeFlow(raw: unknown): ChatFlowDraft | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const flow = raw as Partial<ChatFlowDraft>
  if (flow.kind !== 'payment' && flow.kind !== 'invoice' && flow.kind !== 'budget' && flow.kind !== 'account') {
    return undefined
  }
  return {
    kind: flow.kind,
    movement: flow.movement === 'income' || flow.movement === 'expense' ? flow.movement : '',
    detail: String(flow.detail || ''),
    product: String(flow.product || ''),
    quantity: Number(flow.quantity) > 0 ? Number(flow.quantity) : 0,
    total: Number(flow.total) || 0,
    date: String(flow.date || ''),
    paymentMethodId: String(flow.paymentMethodId || ''),
    accountId: String(flow.accountId || ''),
    budgetItemId: String(flow.budgetItemId || ''),
    categoryId: typeof flow.categoryId === 'number' ? flow.categoryId : null,
  }
}

function normalizeOptions(raw: unknown): ChatOption[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => {
      const row = item as { rowId?: string; id?: string; title?: string; description?: string; flow?: unknown }
      const id = String(row.rowId || row.id || row.title || '').trim()
      const title = String(row.title || row.description || id).trim()
      const description = String(row.description || '').trim()
      const flow = normalizeFlow(row.flow)
      return flow ? { id, title, description, flow } : { id, title, description }
    })
    .filter((opt) => opt.id || opt.title)
}

function usesButtons(options: ChatOption[] | undefined) {
  return !!options && options.length > 0 && options.length <= 3 && options.every((opt) => opt.title.length <= 20)
}

function pendingChoices(msg: ChatMessage) {
  return !msg.fromUser && !!msg.options?.length && !msg.optionsUsed
}

function errorText(error: unknown) {
  const message = error instanceof Error ? error.message : ''
  if (!message || message.startsWith('Cannot read properties')) return 'No pude enviar el mensaje.'
  return message
}

function readReply(data: unknown) {
  if (typeof data === 'string') return { text: data, options: [] as ChatOption[] }
  if (data && typeof data === 'object') {
    const body = data as { content?: unknown; options?: unknown; message?: unknown; detail?: unknown }
    if (typeof body.content === 'string') {
      return { text: body.content, options: normalizeOptions(body.options) }
    }
    if (typeof body.detail === 'string' && body.detail) return { text: body.detail, options: [] as ChatOption[] }
  }
  return { text: 'No recibí una respuesta.', options: [] as ChatOption[] }
}

function lockOptions() {
  messages.value.forEach((msg) => {
    if (!msg.fromUser && msg.options?.length) msg.optionsUsed = true
  })
}

function pushAssistant(data: unknown) {
  const reply = readReply(data)
  messages.value.push({
    text: reply.text,
    fromUser: false,
    time: clock(),
    options: reply.options,
  })
}

function scrollToBottom() {
  nextTick(() => {
    const el = chatMessagesRef.value
    if (el) el.scrollTop = el.scrollHeight
  })
}

function openChat() {
  showHelpModal.value = false
  isVisible.value = true
  const draft = launcherText.value
  launcherText.value = ''
  launcherRef.value?.blur()
  if (draft.trim() && !newMessage.value.trim()) newMessage.value = draft
  nextTick(() => composerRef.value?.focus())
  scrollToBottom()
}

function submitFromLauncher() {
  const text = launcherText.value.trim()
  launcherText.value = ''
  openChat()
  if (text) deliver(text)
}

function onEscape() {
  if (listMessage.value) {
    listMessage.value = null
    return
  }
  isVisible.value = false
}

function openList(msg: ChatMessage) {
  if (sending.value || msg.optionsUsed) return
  listQuery.value = ''
  listMessage.value = msg
}

async function deliver(text: string, display = text) {
  const prompt = text.trim()
  const label = display.trim()
  if (!prompt || sending.value) return
  sending.value = true
  listMessage.value = null
  lockOptions()
  messages.value.push({ text: label, fromUser: true, time: clock() })
  scrollToBottom()
  try {
    const { data } = await send({ text: prompt })
    pushAssistant(data)
  } catch (e: unknown) {
    messages.value.push({
      text: errorText(e),
      fromUser: false,
      time: clock(),
    })
  } finally {
    sending.value = false
    scrollToBottom()
  }
}

function sendMessage() {
  const text = newMessage.value
  if (!text.trim() || sending.value) return
  newMessage.value = ''
  deliver(text)
}

watch([showIncome, showExpense], ([income, expense], previous) => {
  const wasOpen = previous?.[0] || previous?.[1]
  if (wasOpen && !income && !expense) isVisible.value = true
})

function openFlow(flow: ChatFlowDraft) {
  if (flow.kind === 'invoice') {
    isVisible.value = false
    router.push({ name: 'generar-factura' })
    return
  }
  if (flow.kind === 'budget') {
    isVisible.value = false
    router.push({ name: 'budgets' })
    return
  }
  if (flow.kind === 'account') {
    isVisible.value = false
    router.push({ name: 'accounts' })
    return
  }
  isVisible.value = false
  paymentDraft.value = flow
  showIncome.value = flow.movement === 'income'
  showExpense.value = flow.movement !== 'income'
}

function onFlowSaved() {
  messages.value.forEach((msg) => {
    if (msg.options?.some((opt) => opt.flow?.kind === 'payment')) msg.optionsUsed = true
  })
  messages.value.push({ text: 'Respuesta enviada', fromUser: true, time: clock() })
  isVisible.value = true
  scrollToBottom()
}

async function chooseOption(msg: ChatMessage | null, opt: ChatOption) {
  if (!msg || sending.value || msg.optionsUsed) return
  if (opt.flow) {
    openFlow(opt.flow)
    return
  }
  await deliver(opt.id || opt.title, opt.title || opt.id)
}

const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (err) => reject(err)
  })

const MAX_IMAGE_SIZE = 800 * 1024

async function sendImage() {
  const file = image.value
  if (!file || sending.value) return
  if (file.size > MAX_IMAGE_SIZE) {
    messages.value.push({
      text: 'La imagen excede el tamaño máximo de 800 KB.',
      fromUser: false,
      time: clock(),
    })
    image.value = undefined
    scrollToBottom()
    return
  }
  sending.value = true
  listMessage.value = null
  lockOptions()
  messages.value.push({
    text: '',
    fromUser: true,
    time: clock(),
    image: URL.createObjectURL(file),
  })
  scrollToBottom()
  try {
    const { data } = await sendImageAPI(await toBase64(file))
    pushAssistant(data)
  } catch (e: unknown) {
    messages.value.push({
      text: errorText(e),
      fromUser: false,
      time: clock(),
    })
  } finally {
    image.value = undefined
    sending.value = false
    scrollToBottom()
  }
}
</script>

<style lang="scss" scoped>
.chat-input-content {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  border-radius: 24px;
  background: #f0f2f5;
  border: 0;
  overflow: hidden;
}

.chat-open-input {
  width: 100%;
  min-width: 0;
  background: transparent;
  border: 0;
  font-size: 14px;
  padding: 8px 4px;
  color: #111b21;
}

.chat-open-input:focus {
  outline: none;
}

.wa-overlay {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(11, 20, 26, 0.45);
}

.wa-window {
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(440px, 100%);
  height: min(760px, calc(100dvh - 48px));
  overflow: hidden;
  border-radius: 8px;
  background: #efeae2;
  box-shadow: 0 16px 48px rgba(11, 20, 26, 0.28);
}

.wa-header {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 60px;
  padding: 8px 8px 8px 14px;
  background: #008069;
  color: #fff;
}

.wa-avatar {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #00a884;
  flex: 0 0 auto;
}

.wa-header-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;

  strong {
    font-size: 16px;
    font-weight: 500;
    line-height: 1.2;
  }

  span {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.8);
  }
}

.wa-header-close {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #fff;
  cursor: pointer;
}

.wa-thread {
  flex: 1;
  overflow-y: auto;
  padding: 12px 12px 8px;
  background-color: #efeae2;
  background-image: radial-gradient(rgba(17, 27, 33, 0.045) 0.8px, transparent 0.8px);
  background-size: 16px 16px;
}

.wa-day {
  width: fit-content;
  margin: 0 auto 12px;
  padding: 5px 12px;
  border-radius: 8px;
  background: #fff;
  color: #54656f;
  font-size: 12px;
  box-shadow: 0 1px 0.5px rgba(11, 20, 26, 0.13);
}

.wa-row {
  display: flex;
  margin-bottom: 8px;
}

.wa-row--in {
  justify-content: flex-start;
  padding-left: 8px;
}

.wa-row--out {
  justify-content: flex-end;
  padding-right: 8px;
}

.wa-card {
  position: relative;
  max-width: min(85%, 340px);
  padding: 6px 8px 4px;
  box-shadow: 0 1px 0.5px rgba(11, 20, 26, 0.13);
}

.wa-card--in {
  background: #fff;
  border-radius: 0 8px 8px 8px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -8px;
    border-width: 0 8px 10px 0;
    border-style: solid;
    border-color: transparent #fff transparent transparent;
  }
}

.wa-card--out {
  background: #d9fdd3;
  border-radius: 8px 0 8px 8px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: -8px;
    border-width: 0 0 10px 8px;
    border-style: solid;
    border-color: transparent transparent transparent #d9fdd3;
  }
}

.wa-text {
  margin: 0;
  color: #111b21;
  font-size: 14.2px;
  line-height: 1.35;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  a {
    color: #027eb5;
  }

  strong {
    font-weight: 700;
  }

  em {
    font-style: italic;
  }

  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.92em;
  }
}

.wa-card img {
  display: block;
  max-width: 240px;
  max-height: 240px;
  margin-bottom: 4px;
  border-radius: 6px;
  object-fit: cover;
}

.wa-meta {
  float: right;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin: 6px 0 0 10px;
  color: #667781;
  font-size: 11px;
  line-height: 15px;

  svg {
    color: #8696a0;
  }
}

.wa-meta--alone {
  float: none;
  justify-content: flex-end;
  margin: 0;
}

.wa-choices {
  margin: 4px -8px -4px;
  border-top: 1px solid rgba(17, 27, 33, 0.08);
}

.wa-choice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-top: 1px solid rgba(17, 27, 33, 0.08);
  background: transparent;
  color: #00a884;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:first-child {
    border-top: 0;
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
}

.wa-typing {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 58px;
  min-height: 32px;
  padding: 10px 12px;

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #8696a0;
    animation: wa-dot 1.2s infinite;

    &:nth-child(2) {
      animation-delay: 0.15s;
    }

    &:nth-child(3) {
      animation-delay: 0.3s;
    }
  }
}

@keyframes wa-dot {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-2px);
  }
}

.wa-composer {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 8px 10px 10px;
  background: #f0f2f5;
}

.wa-field {
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 42px;
  padding: 0 14px;
  border-radius: 21px;
  background: #fff;

  input {
    width: 100%;
    border: 0;
    outline: none;
    background: transparent;
    color: #111b21;
    font-size: 15px;
  }
}

.wa-icon-btn,
.wa-send {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border: 0;
  cursor: pointer;
}

.wa-icon-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: transparent;
  color: #54656f;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
}

.wa-send {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #00a884;
  color: #fff;

  &:disabled {
    background: #8696a0;
    cursor: default;
  }
}

.wa-attach {
  width: auto;
}

.wa-attach :deep(.va-file-upload__field) {
  min-height: 0;
  padding: 0;
  border: 0;
  background: transparent;
}

.wa-sheet-backdrop {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: flex-end;
  background: rgba(11, 20, 26, 0.45);
}

.wa-sheet {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 78%;
  border-radius: 12px 12px 0 0;
  background: #fff;
  animation: wa-up 0.22s ease;
}

@keyframes wa-up {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.wa-sheet-head {
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 56px;
  padding: 6px 12px;
  color: #111b21;

  strong {
    font-size: 18px;
    font-weight: 500;
  }
}

.wa-search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 16px 8px;
  padding: 0 12px;
  min-height: 38px;
  border-radius: 8px;
  background: #f0f2f5;
  color: #54656f;

  input {
    width: 100%;
    border: 0;
    outline: none;
    background: transparent;
    color: #111b21;
    font-size: 15px;
  }
}

.wa-section {
  margin: 4px 0 0;
  padding: 8px 16px 4px;
  color: #008069;
  font-size: 14px;
}

.wa-sheet-list {
  overflow-y: auto;
  padding-bottom: 12px;
}

.wa-empty {
  margin: 0;
  padding: 16px;
  color: #667781;
  text-align: center;
}

.wa-option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #f0f2f5;
  background: #fff;
  text-align: left;
  cursor: pointer;

  span {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  strong {
    color: #111b21;
    font-size: 16px;
    font-weight: 400;
  }

  small {
    margin-top: 2px;
    color: #667781;
    font-size: 13px;
  }

  &:hover {
    background: #f5f6f6;

    .wa-radio {
      border-color: #00a884;
    }
  }
}

.wa-radio {
  width: 20px;
  height: 20px;
  margin-left: auto;
  border: 2px solid #8696a0;
  border-radius: 50%;
  flex: 0 0 auto;
}

@media (max-width: 820px) {
  .wa-overlay {
    padding: 0;
    align-items: stretch;
  }

  .wa-window {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .wa-sheet,
  .wa-typing span {
    animation: none;
  }
}
</style>
